import { Component, input, signal, ContentChildren, QueryList, AfterContentInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StepperStepComponent } from './stepper-step.component';

@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="w-full" [attr.aria-label]="'Stepper with ' + steps().length + ' steps'">
      <ol class="flex items-center justify-between mb-8">
        @for (step of steps(); track step.id; let i = $index) {
          <li 
            [class]="getStepItemClasses(i, step)"
            [attr.aria-current]="activeIndex() === i ? 'step' : null">
            <div class="flex items-center">
              <div [class]="getStepCircleClasses(i, step)">
                @if (step.completed) {
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                } @else {
                  <span>{{ i + 1 }}</span>
                }
              </div>
              <div class="ml-4">
                <h3 [class]="getStepTitleClasses(i, step)">{{ step.title }}</h3>
                @if (step.description) {
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ step.description }}</p>
                }
              </div>
            </div>
            @if (i < steps().length - 1) {
              <div [class]="getConnectorClasses(i)"></div>
            }
          </li>
        }
      </ol>
      
      <div class="stepper-content">
        @for (step of steps(); track step.id; let i = $index) {
          <div 
            [attr.hidden]="activeIndex() !== i"
            [attr.aria-labelledby]="'step-' + i">
            <ng-container *ngTemplateOutlet="step.content"></ng-container>
          </div>
        }
      </div>
      
      <div class="flex items-center justify-between mt-6">
        <button
          type="button"
          [disabled]="activeIndex() === 0"
          (click)="previous()"
          [class]="getButtonClasses('prev')">
          Previous
        </button>
        <button
          type="button"
          [disabled]="activeIndex() === steps().length - 1"
          (click)="next()"
          [class]="getButtonClasses('next')">
          {{ activeIndex() === steps().length - 1 ? 'Finish' : 'Next' }}
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class StepperComponent implements AfterContentInit {
  activeIndex = input<number>(0);
  linear = input<boolean>(false);
  orientation = input<'horizontal' | 'vertical'>('horizontal');

  @ContentChildren(StepperStepComponent) stepComponents!: QueryList<StepperStepComponent>;
  
  steps = signal<StepperStep[]>([]);
  currentIndex = signal(0);

  ngAfterContentInit(): void {
    const stepList = this.stepComponents.map((step, index) => ({
      id: step.id() || `step-${index}`,
      title: step.title(),
      description: step.description(),
      completed: step.completed(),
      content: step.contentTemplate
    }));
    this.steps.set(stepList);
    this.currentIndex.set(this.activeIndex());
  }

  next(): void {
    if (this.currentIndex() < this.steps().length - 1) {
      this.currentIndex.update(i => i + 1);
    }
  }

  previous(): void {
    if (this.currentIndex() > 0) {
      this.currentIndex.update(i => i - 1);
    }
  }

  goToStep(index: number): void {
    if (!this.linear() || this.isStepAccessible(index)) {
      this.currentIndex.set(index);
    }
  }

  isStepAccessible(index: number): boolean {
    if (!this.linear()) return true;
    return index <= this.currentIndex() || this.steps()[index - 1]?.completed;
  }

  getStepItemClasses(index: number, step: StepperStep): string {
    const classes = ['flex', 'items-center', 'flex-1'];
    if (this.currentIndex() === index) {
      classes.push('text-blue-600', 'dark:text-blue-400');
    } else if (step.completed) {
      classes.push('text-green-600', 'dark:text-green-400');
    } else {
      classes.push('text-gray-500', 'dark:text-gray-400');
    }
    return classes.join(' ');
  }

  getStepCircleClasses(index: number, step: StepperStep): string {
    const classes = [
      'flex',
      'items-center',
      'justify-center',
      'w-10',
      'h-10',
      'rounded-full',
      'font-semibold',
      'transition-all',
      'duration-200'
    ];

    if (this.currentIndex() === index) {
      classes.push('bg-blue-600', 'text-white', 'ring-4', 'ring-blue-200', 'dark:ring-blue-900/30');
    } else if (step.completed) {
      classes.push('bg-green-600', 'text-white');
    } else {
      classes.push('bg-gray-200', 'dark:bg-gray-700', 'text-gray-600', 'dark:text-gray-400');
    }

    return classes.join(' ');
  }

  getStepTitleClasses(index: number, step: StepperStep): string {
    const classes = ['text-sm', 'font-medium'];
    if (this.currentIndex() === index) {
      classes.push('text-blue-600', 'dark:text-blue-400');
    }
    return classes.join(' ');
  }

  getConnectorClasses(index: number): string {
    const classes = ['flex-1', 'h-0.5', 'mx-4', 'transition-colors', 'duration-200'];
    if (this.currentIndex() > index) {
      classes.push('bg-green-600');
    } else {
      classes.push('bg-gray-200', 'dark:bg-gray-700');
    }
    return classes.join(' ');
  }

  getButtonClasses(type: 'prev' | 'next'): string {
    const classes = [
      'px-4',
      'py-2',
      'rounded-md',
      'font-medium',
      'transition-colors',
      'duration-200',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed'
    ];

    if (type === 'next') {
      classes.push('bg-blue-600', 'text-white', 'hover:bg-blue-700', 'dark:bg-blue-500', 'dark:hover:bg-blue-600');
    } else {
      classes.push('bg-gray-100', 'text-gray-700', 'hover:bg-gray-200', 'dark:bg-gray-700', 'dark:text-gray-300', 'dark:hover:bg-gray-600');
    }

    return classes.join(' ');
  }
}

interface StepperStep {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  content: any;
}

